import { useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, ShieldCheck, LogOut, LogIn, Home, Inbox, Store, Info, LayoutDashboard, Briefcase, Plus } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { LANGS, stripLang, useT } from "@/lib/i18n";
import { useAuth, startLogin } from "@/lib/auth";
import { useCatalog, useJobsOn } from "@/lib/data";
import { Seo } from "@/components/Seo";

export const Logo = () => {
  const { lp } = useT();
  return (
    <Link to={lp("/")} className="flex items-center gap-2" data-testid="header-logo-link">
      <span className="grid h-9 w-9 place-items-center rounded-xl bg-saffron text-white shadow-sm">
        <ShieldCheck className="h-5 w-5" strokeWidth={2.2} />
      </span>
      <span className="font-display text-lg font-extrabold leading-none tracking-tight text-ink">
        Moga<span className="text-saffron">360</span>
      </span>
    </Link>
  );
};

export const LangSwitch = () => {
  const { lang } = useT();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const go = (code) => {
    const base = stripLang(pathname);
    navigate((code === "en" ? base : `/${code}${base === "/" ? "" : base}`) + search);
  };
  return (
    <div className="flex items-center rounded-xl border border-line bg-white p-0.5 text-xs font-semibold" data-testid="language-switch">
      {LANGS.map((l) => (
        <button key={l.code} onClick={() => go(l.code)} data-testid={`lang-${l.code}-btn`}
          className={`rounded-lg px-1.5 py-1.5 transition-colors sm:px-2 ${lang === l.code ? "bg-ink text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          <span className="sm:hidden">{l.short}</span><span className="hidden sm:inline">{l.label}</span>
        </button>
      ))}
    </div>
  );
};

const NavItem = ({ to, icon: Icon, label, onClick, testid }) => (
  <Link to={to} onClick={onClick} data-testid={testid}
    className="flex items-center gap-3 rounded-xl px-3 py-3 text-base font-medium text-ink hover:bg-saffron-50">
    <Icon className="h-5 w-5 text-saffron" /> {label}
  </Link>
);

const NavMenu = () => {
  const { t, lp } = useT();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname, search } = useLocation();
  const close = () => setOpen(false);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <button onClick={() => setOpen(true)} className="grid h-10 w-10 place-items-center rounded-xl border border-line bg-white" data-testid="nav-menu-btn" aria-label="Menu">
        <Menu className="h-5 w-5" />
      </button>
      <SheetContent side="right" className="w-[300px] bg-paper" data-testid="nav-menu">
        <SheetHeader><SheetTitle className="text-left"><Logo /></SheetTitle></SheetHeader>
        {user && <p className="mt-4 truncate px-3 text-sm text-slate-500" data-testid="nav-user-email">{user.email || user.phone}</p>}
        <nav className="mt-4 space-y-1">
          <NavItem to={lp("/")} icon={Home} label={t("nav_home")} onClick={close} testid="nav-home-link" />
          {useJobsOn() && <NavItem to={lp("/jobs")} icon={Briefcase} label={t("nav_jobs")} onClick={close} testid="nav-jobs-link" />}
          <NavItem to={lp("/my")} icon={Inbox} label={t("nav_my")} onClick={close} testid="nav-my-link" />
          <NavItem to={lp("/owner")} icon={Store} label={t("nav_owner")} onClick={close} testid="nav-owner-link" />
          {user?.role === "admin" && <NavItem to={lp("/admin")} icon={LayoutDashboard} label={t("nav_admin")} onClick={close} testid="nav-admin-link" />}
          <NavItem to={lp("/why-moga-360")} icon={ShieldCheck} label={t("nav_why")} onClick={close} testid="nav-why-link" />
          <NavItem to={lp("/how-it-works")} icon={Info} label={t("nav_how")} onClick={close} testid="nav-how-link" />
        </nav>
        <div className="mt-6 border-t border-line pt-4">
          {user ? (
            <button onClick={() => { logout(); close(); }} className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-base font-medium text-slate-600 hover:bg-slate-100" data-testid="nav-signout-btn">
              <LogOut className="h-5 w-5" /> {t("nav_signout")}
            </button>
          ) : (
            <button onClick={() => startLogin(pathname + search)} className="flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-3 py-3 font-semibold text-white" data-testid="nav-signin-btn">
              <LogIn className="h-5 w-5" /> {t("nav_signin")}
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const DesktopNav = () => {
  const { t, lp } = useT();
  const { user } = useAuth();
  const jobsOn = useJobsOn();
  const links = [["/browse", "find_business"], ...(jobsOn ? [["/jobs", "nav_jobs"]] : []), ["/my", "nav_my"], ["/owner", "nav_owner"], ["/why-moga-360", "nav_why"], ["/how-it-works", "nav_how"]];
  if (user?.role === "admin") links.push(["/admin", "nav_admin"]);
  return (
    <nav className="hidden items-center gap-0.5 xl:flex" data-testid="desktop-nav">
      {links.map(([p, k]) => (
        <NavLink key={p} to={lp(p)} data-testid={`desktop-nav-${k}`}
          className={({ isActive }) => `whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-saffron-50 text-saffron-dark" : "text-slate-600 hover:text-ink"}`}>
          {t(k)}
        </NavLink>
      ))}
    </nav>
  );
};

const DesktopAuth = () => {
  const { t } = useT();
  const { user, logout } = useAuth();
  const { pathname, search } = useLocation();
  return user ? (
    <button onClick={logout} title={user.email || user.phone} data-testid="desktop-signout-btn"
      className="hidden items-center gap-2 whitespace-nowrap rounded-xl border border-line bg-white px-3 py-2 text-sm font-semibold text-slate-600 xl:flex">
      <LogOut className="h-4 w-4" /> {t("nav_signout")}
    </button>
  ) : (
    <button onClick={() => startLogin(pathname + search)} data-testid="desktop-signin-btn"
      className="hidden items-center gap-2 whitespace-nowrap rounded-xl bg-ink px-4 py-2 text-sm font-semibold text-white xl:flex">
      <LogIn className="h-4 w-4" /> {t("nav_signin")}
    </button>
  );
};

const Footer = () => {
  const { t, tn, lp } = useT();
  const { categories, towns } = useCatalog();
  const town = towns[0];
  return (
    <footer className="mt-12 border-t border-line print:hidden" data-testid="site-footer">
      <div className="mx-auto max-w-6xl px-5 py-8 text-sm text-slate-500 lg:px-8">
      <div className="flex flex-wrap gap-x-5 gap-y-2 font-medium">
        <Link to={lp("/why-moga-360")} className="hover:text-ink" data-testid="footer-why-link">{t("nav_why")}</Link>
        <Link to={lp("/how-it-works")} className="hover:text-ink" data-testid="footer-how-link">{t("nav_how")}</Link>
        <Link to={lp("/removed")} className="hover:text-ink" data-testid="footer-removed-link">{t("nav_removed")}</Link>
        <Link to={lp("/owner")} className="hover:text-ink" data-testid="footer-owner-link">{t("list_free")}</Link>
        {["contact", "privacy", "terms", "refunds"].map((k) => (
          <Link key={k} to={lp(`/${k}`)} className="hover:text-ink" data-testid={`footer-${k}-link`}>{t(`legal_${k}`)}</Link>
        ))}
      </div>
      {town && (
        <p className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs" data-testid="footer-popular">
          <b className="text-slate-600">{t("popular_in", { town: tn(town.name) })}:</b>
          {categories.slice(0, 12).map((c) => <Link key={c.id} to={lp(`/${town.slug}/${c.slug}`)} className="hover:text-ink" data-testid={`footer-popular-${c.slug}`}>{tn(c.name)}</Link>)}
        </p>
      )}
      <p className="mt-4 text-xs">© {new Date().getFullYear()} Moga 360 · {t("tagline")}</p>
      </div>
    </footer>
  );
};

export default function Layout({ children, narrow = false, footer = true, seo }) {
  const { t, lp } = useT();
  return (
    <div className="min-h-screen bg-paper">
      <Seo {...(seo || {})} />
      <header className="sticky top-0 z-40 border-b border-line bg-paper/90 backdrop-blur-md print:hidden">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-1.5 px-3 py-3 sm:gap-2 sm:px-4 lg:px-8">
          <Logo />
          <DesktopNav />
          <div className="flex items-center gap-1.5 sm:gap-2">
            <LangSwitch />
            <Link to={lp("/owner")} data-testid="header-list-business-mobile-btn" aria-label={t("list_your_business")}
              className="flex items-center gap-1 whitespace-nowrap rounded-lg bg-saffron px-2.5 py-2 text-xs font-bold text-white sm:hidden">
              <Plus className="h-3.5 w-3.5" strokeWidth={3} />{t("list_short")}
            </Link>
            <Link to={lp("/owner")} data-testid="header-list-business-btn"
              className="hidden items-center whitespace-nowrap rounded-xl bg-saffron px-4 py-2 text-sm font-semibold text-white transition-colors duration-200 hover:bg-saffron-dark sm:flex">
              {t("list_your_business")}
            </Link>
            <DesktopAuth />
            <div className="xl:hidden"><NavMenu /></div>
          </div>
        </div>
      </header>
      <main className={`animate-rise mx-auto w-full ${narrow ? "max-w-3xl" : "max-w-6xl"}`}>{children}</main>
      {footer && <Footer />}
    </div>
  );
}
